import { UpgradeSubscriptionModal } from '@components'

import { useModal } from '@billy/design-system'
import { Button, Icon, Menu } from '@shinetools/lumen-react'
import { ArrowDownToLine, ArrowUpFromLine, EllipsisIcon, FileText, Trash2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { DeleteContactConfirmationModal } from '@features/contacts/elements/DeleteContactConfirmationModal'
import { Contact } from '@features/contacts/types/contact'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { Scope } from '../../../../../../../../../../enums/scope'
import { Side } from '../../../../../../../../../../enums/side'
import { TrackingContext } from '../../../../../../../../../../enums/trackingContext'
import { useOrganizationViewUrl } from '../../../../../../../../../../hooks/routing/useOrganizationViewUrl'
import { useComponentId } from '../../../../../../../../../../hooks/useComponentId'
import { isAuthorized } from '../../../../../../../../../../utils/isAuthorized'
import { reactRoute } from '../../../../../../../../../../utils/routing/reactRoute'
import { SupplierPaymentModal } from './elements/SupplierPaymentModal'
import { SupplierStatementExportModal } from './elements/SupplierStatementExportModal'

interface HeaderSupplierDetailMenuProps {
  contact: Contact
}

export const HeaderSupplierDetailMenu = ({ contact }: HeaderSupplierDetailMenuProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [paymentSide, setPaymentSide] = useState(Side.Debit)
  const upgradeSubscriptionModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openSupplierPaymentModal } = useModal(ModalId.SupplierPaymentModal)
  const { open: openSupplierStatementExportModal } = useModal(ModalId.SupplierStatementExportModal)
  const { open: openUpgradeSubscriptionModal } = useModal(upgradeSubscriptionModalId)
  const { open: openDeleteContactConfirmationModal } = useModal(ModalId.DeleteContactConfirmationModal)
  const { url: suppliersUrl } = useOrganizationViewUrl(reactRoute.organization.suppliers.getMainRoute())

  const handlePaymentClick = useCallback(
    (side: Side) => () => {
      setPaymentSide(side)
      openSupplierPaymentModal()
    },
    [openSupplierPaymentModal],
  )

  const handleStatementClick = useCallback(() => {
    if (!isAuthorized(Scope.ContactStatementRead)) {
      openUpgradeSubscriptionModal()
      return
    }

    openSupplierStatementExportModal()
  }, [openSupplierStatementExportModal, openUpgradeSubscriptionModal])

  const handleDeleteClick = useCallback(() => {
    openDeleteContactConfirmationModal()
  }, [openDeleteContactConfirmationModal])

  const handleDeleteSuccess = useCallback(() => {
    if (suppliersUrl) {
      navigate(suppliersUrl)
    }
  }, [navigate, suppliersUrl])

  return (
    <>
      <Menu
        aria-label={t('more')}
        placement="bottom-start"
        size="medium"
        trigger={<Button aria-label={t('more')} icon={<Icon icon={EllipsisIcon} />} variant="outlined" />}
        width="hug"
      >
        <Menu.Item
          icon={<Icon icon={ArrowDownToLine} size="small" />}
          label={t('contact.index.payment.ingoing')}
          onClick={handlePaymentClick(Side.Debit)}
        />
        <Menu.Item
          icon={<Icon icon={ArrowUpFromLine} size="small" />}
          label={t('contact.index.payment.outgoing')}
          onClick={handlePaymentClick(Side.Credit)}
        />
        <Menu.Item
          icon={<Icon icon={FileText} size="small" />}
          label={t('contact.index.account_statement')}
          onClick={handleStatementClick}
        />
        <Menu.Item
          destructive
          icon={<Icon icon={Trash2} size="small" />}
          label={t('delete')}
          onClick={handleDeleteClick}
        />
      </Menu>
      <SupplierPaymentModal contact={contact} id={ModalId.SupplierPaymentModal} side={paymentSide} />
      <SupplierStatementExportModal contact={contact} id={ModalId.SupplierStatementExportModal} />
      <UpgradeSubscriptionModal
        id={upgradeSubscriptionModalId}
        trackingContext={TrackingContext.SupplierAccountStatement}
      />
      <DeleteContactConfirmationModal
        onDeleteContactSuccess={handleDeleteSuccess}
        selectedContact={{ id: contact.id, name: contact.name }}
        variant="supplierDetail"
      />
    </>
  )
}
